import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Spinner, Alert, Input, Button,
    Nav, NavItem, NavLink, TabContent, TabPane,
    Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import classnames from "classnames";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── types ── */
interface DemandeMigration {
    id: number;
    idsale: number;
    phone_line?: string;
    phone_line_formatted?: string;
    wholesale?: string;
    customer?: string;
    current_offer?: string;
    current_amt_recur?: number;
    offer: string;
    offer_label?: string;
    amt_recur: number;
    date_migration: string;
    date_created: string;
    date_executed?: string | null;
    status: 0 | 1 | 2 | 3;
    status_label: string;
    idproduct_order?: number;
    error_message?: string | null;
    cancellable: boolean;
}

interface DemandesCounts {
    total: number;
    pending: number;
    in_progress: number;
    done: number;
    error: number;
}

interface DemandesPayload {
    items: DemandeMigration[];
    counts: DemandesCounts;
}

/* ── helpers ── */
const STATUS_COLOR: Record<number, string> = {
    0: "warning",
    1: "info",
    2: "success",
    3: "danger",
};

const STATUS_ICON: Record<number, string> = {
    0: "ri-time-line",
    1: "ri-loader-4-line",
    2: "ri-checkbox-circle-line",
    3: "ri-close-circle-line",
};

const formatDate = (raw?: string | null) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
    });
};

const formatDateTime = (raw?: string | null) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

/* ── composant ── */
const MesDemandes = () => {
    const [items, setItems]   = useState<DemandeMigration[]>([]);
    const [counts, setCounts] = useState<DemandesCounts>({ total: 0, pending: 0, in_progress: 0, done: 0, error: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState<string | null>(null);

    const [statusFilter, setStatusFilter] = useState<"all" | 0 | 1 | 2 | 3>("all");
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState<"migrations" | "options">("migrations");

    const [cancelLoadingId, setCancelLoadingId] = useState<number | null>(null);
    const [detailModal, setDetailModal] = useState(false);
    const [detailItem, setDetailItem] = useState<DemandeMigration | null>(null);

    const fetchList = async () => {
        try {
            setLoading(true);
            const res: any = await axios.post(
                "/apiV2/",
                { action: "mesdemandes", subaction: "list" },
                apiV2PostConfig()
            );
            const payload: DemandesPayload = res?.data ?? res ?? { items: [], counts: { total: 0, pending: 0, in_progress: 0, done: 0, error: 0 } };
            setItems(payload.items || []);
            setCounts(payload.counts || { total: 0, pending: 0, in_progress: 0, done: 0, error: 0 });
            setError(null);
        } catch (e: any) {
            setError(typeof e === "string" ? e : e?.message || "Erreur lors de la récupération des demandes.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const cancelDemande = async (d: DemandeMigration) => {
        if (!window.confirm(`Annuler la demande de migration vers « ${d.offer_label || d.offer} » ?`)) return;
        setCancelLoadingId(d.id);
        try {
            await axios.post(
                "/apiV2/",
                { action: "mesdemandes", subaction: "cancel", id: d.id },
                apiV2PostConfig()
            );
            await fetchList();
            setDetailModal(false);
        } catch (e: any) {
            setError(typeof e === "string" ? e : e?.message || "Erreur lors de l'annulation.");
        } finally {
            setCancelLoadingId(null);
        }
    };

    const openDetail = (d: DemandeMigration) => {
        setDetailItem(d);
        setDetailModal(true);
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return items.filter(d => {
            if (statusFilter !== "all" && d.status !== statusFilter) return false;
            if (!q) return true;
            return (
                (d.offer || "").toLowerCase().includes(q) ||
                (d.offer_label || "").toLowerCase().includes(q) ||
                (d.phone_line_formatted || "").toLowerCase().includes(q) ||
                (d.phone_line || "").toLowerCase().includes(q) ||
                (d.customer || "").toLowerCase().includes(q)
            );
        });
    }, [items, search, statusFilter]);

    const widgets = [
        { label: "Total",        counter: counts.total,       icon: "ri-inbox-archive-line",  color: "primary" },
        { label: "En attente",   counter: counts.pending,     icon: "ri-time-line",           color: "warning" },
        { label: "En cours",     counter: counts.in_progress, icon: "ri-loader-4-line",       color: "info"    },
        { label: "Terminées",    counter: counts.done,        icon: "ri-checkbox-circle-line",color: "success" },
        { label: "En erreur",    counter: counts.error,       icon: "ri-close-circle-line",   color: "danger"  },
    ];

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Mes Demandes" pageTitle="Espace Client" />

                    {error && (
                        <Alert color="danger" className="mb-4" toggle={() => setError(null)}>
                            <i className="ri-error-warning-line me-2"></i>{error}
                        </Alert>
                    )}

                    {/* ── KPI ── */}
                    <Row>
                        {widgets.map((w, i) => (
                            <Col xxl sm={6} md={4} lg key={i}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{w.label}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <CountUp start={0} end={w.counter} duration={2} />
                                                </h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={`avatar-title rounded-circle fs-4 bg-${w.color}-subtle text-${w.color}`}>
                                                    <i className={w.icon}></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Tabs principaux ── */}
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="border-0 pb-0">
                                    <Nav tabs className="nav-tabs-custom nav-primary mb-0">
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === "migrations" })}
                                                onClick={() => setActiveTab("migrations")}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <i className="ri-exchange-line me-1"></i>
                                                Migrations de forfait
                                                <Badge color="primary" pill className="ms-2">{counts.total}</Badge>
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink
                                                className={classnames({ active: activeTab === "options" })}
                                                onClick={() => setActiveTab("options")}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <i className="ri-apps-2-line me-1"></i>
                                                Options & services
                                                <Badge color="secondary" pill className="ms-2">Bientôt</Badge>
                                            </NavLink>
                                        </NavItem>
                                    </Nav>
                                </CardHeader>

                                <CardBody>
                                    <TabContent activeTab={activeTab}>

                                        {/* ── Tab migrations ── */}
                                        <TabPane tabId="migrations">
                                            <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                                                <div className="search-box">
                                                    <Input
                                                        type="text"
                                                        className="form-control search"
                                                        placeholder="Rechercher un numéro, forfait, client…"
                                                        value={search}
                                                        onChange={e => setSearch(e.target.value)}
                                                        style={{ minWidth: 280 }}
                                                    />
                                                    <i className="ri-search-line search-icon"></i>
                                                </div>
                                                <Nav pills className="nav-pills-custom gap-2 ms-auto">
                                                    {[
                                                        { k: "all",  label: "Tous",         count: counts.total       },
                                                        { k: 0,      label: "En attente",   count: counts.pending     },
                                                        { k: 1,      label: "En cours",     count: counts.in_progress },
                                                        { k: 2,      label: "Terminées",    count: counts.done        },
                                                        { k: 3,      label: "Erreur",       count: counts.error       },
                                                    ].map((t, i) => (
                                                        <NavItem key={i}>
                                                            <NavLink
                                                                className={classnames({ active: statusFilter === (t.k as any) })}
                                                                onClick={() => setStatusFilter(t.k as any)}
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                {t.label}
                                                                <Badge color="light" className="ms-1 text-dark">{t.count}</Badge>
                                                            </NavLink>
                                                        </NavItem>
                                                    ))}
                                                </Nav>
                                            </div>

                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner style={{ color: "#0066A1" }} />
                                                    <p className="text-muted mt-2">Chargement des demandes…</p>
                                                </div>
                                            ) : filtered.length === 0 ? (
                                                <div className="text-center py-5">
                                                    <div className="avatar-md mx-auto mb-3">
                                                        <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                                                            <i className="ri-inbox-line"></i>
                                                        </span>
                                                    </div>
                                                    <h5 className="mt-2">Aucune demande trouvée</h5>
                                                    <p className="text-muted">
                                                        {search || statusFilter !== "all"
                                                            ? "Aucun résultat pour ces filtres."
                                                            : "Vous n'avez pas encore effectué de demande de migration."}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table align-middle table-nowrap mb-0">
                                                        <thead className="table-light text-muted">
                                                            <tr>
                                                                <th>Ligne</th>
                                                                <th>Forfait actuel</th>
                                                                <th>Nouveau forfait</th>
                                                                <th>Date migration</th>
                                                                <th>Créée le</th>
                                                                <th className="text-center">Statut</th>
                                                                <th className="text-center">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filtered.map(d => (
                                                                <tr key={d.id} style={{ cursor: "pointer" }} onClick={() => openDetail(d)}>
                                                                    <td>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <div className="avatar-xs flex-shrink-0">
                                                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-14">
                                                                                    <i className="ri-sim-card-2-line"></i>
                                                                                </span>
                                                                            </div>
                                                                            <div>
                                                                                <div className="fw-semibold">{d.phone_line_formatted || d.phone_line || `#${d.idsale}`}</div>
                                                                                {d.customer && <div className="text-muted fs-12">{d.customer}</div>}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="fs-13">
                                                                        <div className="text-muted">{d.current_offer || "—"}</div>
                                                                        {d.current_amt_recur ? <div className="text-muted fs-12">{d.current_amt_recur.toFixed(2)} € / mois</div> : null}
                                                                    </td>
                                                                    <td className="fs-13">
                                                                        <div className="fw-medium">{d.offer_label || d.offer}</div>
                                                                        {d.amt_recur ? <div className="text-muted fs-12">{d.amt_recur.toFixed(2)} € / mois</div> : null}
                                                                    </td>
                                                                    <td className="text-muted fs-13">{formatDate(d.date_migration)}</td>
                                                                    <td className="text-muted fs-13">{formatDate(d.date_created)}</td>
                                                                    <td className="text-center">
                                                                        <Badge className={`bg-${STATUS_COLOR[d.status]}-subtle text-${STATUS_COLOR[d.status]}`}>
                                                                            <i className={`${STATUS_ICON[d.status]} me-1`}></i>
                                                                            {d.status_label}
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="text-center" onClick={e => e.stopPropagation()}>
                                                                        {d.cancellable ? (
                                                                            <Button
                                                                                color="danger"
                                                                                size="sm"
                                                                                outline
                                                                                disabled={cancelLoadingId === d.id}
                                                                                onClick={() => cancelDemande(d)}
                                                                                title="Annuler la demande"
                                                                            >
                                                                                {cancelLoadingId === d.id
                                                                                    ? <Spinner size="sm" />
                                                                                    : <i className="ri-close-circle-line"></i>
                                                                                }
                                                                            </Button>
                                                                        ) : (
                                                                            <span className="text-muted fs-12">—</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </TabPane>

                                        {/* ── Tab options (placeholder) ── */}
                                        <TabPane tabId="options">
                                            <div className="text-center py-5">
                                                <div className="avatar-lg mx-auto mb-3">
                                                    <span className="avatar-title rounded-circle bg-info-subtle text-info fs-2">
                                                        <i className="ri-apps-2-line"></i>
                                                    </span>
                                                </div>
                                                <h5 className="mt-2">Souscription & résiliation d'options</h5>
                                                <p className="text-muted mb-4">
                                                    Cette section vous permettra bientôt de souscrire ou résilier des options<br />
                                                    sur vos lignes (roaming, data supplémentaire, SVA, etc.) directement depuis votre espace.
                                                </p>
                                                <Badge color="info" className="px-3 py-2 fs-13">
                                                    <i className="ri-rocket-line me-1"></i>
                                                    Fonctionnalité en cours de développement
                                                </Badge>
                                                <Row className="g-3 mt-4">
                                                    {[
                                                        { icon: "ri-global-line",     title: "Roaming international", desc: "Activer/désactiver le roaming" },
                                                        { icon: "ri-database-2-line", title: "Data supplémentaire",   desc: "Booster data ponctuel ou permanent" },
                                                        { icon: "ri-phone-line",      title: "Services à valeur ajoutée", desc: "Annuaire, messagerie vocale…" },
                                                        { icon: "ri-close-circle-line", title: "Résiliation de ligne",  desc: "Demande de résiliation avec préavis" },
                                                    ].map((o, i) => (
                                                        <Col md={6} lg={3} key={i}>
                                                            <div className="border rounded p-3 h-100 text-start opacity-50">
                                                                <div className="avatar-xs mb-2">
                                                                    <span className="avatar-title rounded-circle bg-light text-muted fs-16">
                                                                        <i className={o.icon}></i>
                                                                    </span>
                                                                </div>
                                                                <h6 className="mb-1 fs-14">{o.title}</h6>
                                                                <p className="text-muted fs-12 mb-0">{o.desc}</p>
                                                            </div>
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </div>
                                        </TabPane>

                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                </Container>
            </div>

            {/* ── Modal détail ── */}
            <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="md" centered>
                <ModalHeader toggle={() => setDetailModal(false)} className="border-bottom-dashed">
                    <div className="d-flex align-items-center gap-2">
                        <span className="avatar-xs">
                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                <i className="ri-exchange-line"></i>
                            </span>
                        </span>
                        <div>
                            <div className="fw-semibold fs-15">Demande #{detailItem?.id}</div>
                            {detailItem && (
                                <Badge className={`bg-${STATUS_COLOR[detailItem.status]}-subtle text-${STATUS_COLOR[detailItem.status]} mt-1`}>
                                    <i className={`${STATUS_ICON[detailItem.status]} me-1`}></i>
                                    {detailItem.status_label}
                                </Badge>
                            )}
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="p-3">
                    {detailItem && (
                        <>
                            <div className="bg-light rounded p-3 mb-3">
                                <p className="text-muted fs-11 mb-1 text-uppercase">Ligne concernée</p>
                                <h6 className="mb-0">
                                    <i className="ri-sim-card-2-line me-1 text-primary"></i>
                                    {detailItem.phone_line_formatted || detailItem.phone_line || `#${detailItem.idsale}`}
                                </h6>
                                {detailItem.customer && <p className="text-muted fs-13 mb-0 mt-1">{detailItem.customer}</p>}
                                {detailItem.wholesale && (
                                    <Badge className="bg-info-subtle text-info mt-1">{detailItem.wholesale}</Badge>
                                )}
                            </div>

                            <Row className="g-3">
                                <Col xs={6}>
                                    <p className="text-muted fs-11 mb-1 text-uppercase">Forfait actuel</p>
                                    <p className="mb-0 fw-medium">{detailItem.current_offer || "—"}</p>
                                    {detailItem.current_amt_recur ? (
                                        <p className="text-muted fs-12 mb-0">{detailItem.current_amt_recur.toFixed(2)} € / mois</p>
                                    ) : null}
                                </Col>
                                <Col xs={6}>
                                    <p className="text-muted fs-11 mb-1 text-uppercase">Nouveau forfait</p>
                                    <p className="mb-0 fw-medium text-primary">{detailItem.offer_label || detailItem.offer}</p>
                                    {detailItem.amt_recur ? (
                                        <p className="text-muted fs-12 mb-0">{detailItem.amt_recur.toFixed(2)} € / mois</p>
                                    ) : null}
                                </Col>
                                <Col xs={6}>
                                    <p className="text-muted fs-11 mb-1 text-uppercase">Date de migration</p>
                                    <p className="mb-0 fs-13">{formatDateTime(detailItem.date_migration)}</p>
                                </Col>
                                <Col xs={6}>
                                    <p className="text-muted fs-11 mb-1 text-uppercase">Créée le</p>
                                    <p className="mb-0 fs-13">{formatDateTime(detailItem.date_created)}</p>
                                </Col>
                                {detailItem.date_executed && (
                                    <Col xs={12}>
                                        <p className="text-muted fs-11 mb-1 text-uppercase">Exécutée le</p>
                                        <p className="mb-0 fs-13">{formatDateTime(detailItem.date_executed)}</p>
                                    </Col>
                                )}
                                {detailItem.error_message && (
                                    <Col xs={12}>
                                        <Alert color="danger" className="mb-0">
                                            <i className="ri-error-warning-line me-1"></i>
                                            {detailItem.error_message}
                                        </Alert>
                                    </Col>
                                )}
                                {detailItem.idproduct_order ? (
                                    <Col xs={12}>
                                        <p className="text-muted fs-11 mb-1 text-uppercase">Commande EMA</p>
                                        <Badge className="bg-success-subtle text-success">#{detailItem.idproduct_order}</Badge>
                                    </Col>
                                ) : null}
                            </Row>
                        </>
                    )}
                </ModalBody>
                <ModalFooter className="border-top-dashed">
                    <Button color="light" onClick={() => setDetailModal(false)}>
                        Fermer
                    </Button>
                    {detailItem?.cancellable && (
                        <Button
                            color="danger"
                            onClick={() => detailItem && cancelDemande(detailItem)}
                            disabled={cancelLoadingId === detailItem?.id}
                        >
                            {cancelLoadingId === detailItem?.id
                                ? <><Spinner size="sm" className="me-1" />Annulation…</>
                                : <><i className="ri-close-circle-line me-1"></i>Annuler la demande</>
                            }
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

        </React.Fragment>
    );
};

export default MesDemandes;
